/**
 * EdgeSafeMode — derives global degraded state.
 *
 * Inputs: MobileMatchMode profile/fps, network online, circuit breaker,
 * optional manual stress mode. Pure derivation, no polling.
 */
import { useSyncExternalStore } from "react";
import { subscribeBreaker, isAnyOpen } from "./circuitBreaker";
import { getProfile } from "@/lib/launchHardening/mobileMatchMode";

export type EdgeSafeLevel = "normal" | "soft" | "hard";
export interface EdgeSafeState {
  level: EdgeSafeLevel;
  reason: string;
  online: boolean;
  fps: number;
  breakerOpen: boolean;
  realtimeStable: boolean;
}

let realtimeStable = true;
let manualForce: EdgeSafeLevel | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function setRealtimeStable(stable: boolean) {
  if (realtimeStable !== stable) {
    realtimeStable = stable;
    emit();
  }
}

export function forceEdgeSafeLevel(level: EdgeSafeLevel | null) {
  manualForce = level;
  emit();
}

function compute(): EdgeSafeState {
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  const mmm = getProfile();
  const breakerOpen = isAnyOpen();
  let level: EdgeSafeLevel = "normal";
  let reason = "stable";
  if (!online) { level = "hard"; reason = "offline"; }
  else if (breakerOpen) { level = "hard"; reason = "queries failing"; }
  else if (mmm.profile === "low" || mmm.fps < 35) { level = "soft"; reason = "low perf"; }
  else if (!realtimeStable) { level = "soft"; reason = "realtime degraded"; }
  if (manualForce) { level = manualForce; reason = `forced:${manualForce}`; }
  return { level, reason, online, fps: mmm.fps, breakerOpen, realtimeStable };
}

let cached: EdgeSafeState = compute();

function refresh() {
  const next = compute();
  if (
    next.level !== cached.level ||
    next.online !== cached.online ||
    next.breakerOpen !== cached.breakerOpen ||
    next.realtimeStable !== cached.realtimeStable
  ) {
    cached = next;
    emit();
  } else {
    cached = next;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", refresh);
  window.addEventListener("offline", refresh);
  subscribeBreaker(refresh);
  setInterval(refresh, 5_000);
}

export function getEdgeSafeState(): EdgeSafeState {
  return cached;
}

export function useEdgeSafeMode(): EdgeSafeState {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => cached,
    () => cached,
  );
}

/** Multiplier 0..1 for cinematic/overlay density given current level. */
export function densityMultiplier(level: EdgeSafeLevel = cached.level): number {
  return level === "hard" ? 0.35 : level === "soft" ? 0.65 : 1;
}
